import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { OGE_PYTHON_STEPS } from '../ogePythonPath';
import { taskNavRef } from '../utils/taskNav';

function writeCatalogHref(tagSlug) {
  const p = new URLSearchParams();
  p.append('tag', tagSlug);
  p.append('tag', 'oge-write');
  return `/catalog?${p.toString()}`;
}

export default function OgePythonPathPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startingId, setStartingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/tasks/practice-quizzes/');
        if (!cancelled) setQuizzes(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setError('Не удалось загрузить тесты. Конспекты всё равно можно открыть.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const quizByTitle = useMemo(() => {
    const map = new Map();
    for (const quiz of quizzes) {
      if (quiz?.title) map.set(quiz.title, quiz);
    }
    return map;
  }, [quizzes]);

  async function startQuiz(quiz) {
    if (!quiz?.id) return;
    setStartingId(quiz.id);
    setError('');
    try {
      const res = await api.post(`/tasks/practice-quizzes/${quiz.id}/start/`, {
        shuffle: false,
      });
      const listId = res.data?.list_id;
      const ref = taskNavRef(res.data?.first_task);
      if (listId && ref) {
        navigate(`/work/${ref}?list=${listId}`);
        return;
      }
      setError('Тест создан, но не удалось открыть первую задачу.');
    } catch {
      setError('Не удалось начать тест.');
    } finally {
      setStartingId(null);
    }
  }

  return (
    <div className="oge-path-page">
      <div className="page-header">
        <h1>ОГЭ · Программирование</h1>
        <p className="page-subtitle">
          Иди сверху вниз. В каждом шаге сначала конспект, потом тест, потом короткие задачи
          «напиши программу», если они есть.
        </p>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}

      {loading ? <p className="text-secondary">Загрузка тестов…</p> : null}

      <ol className="oge-path-list">
        {OGE_PYTHON_STEPS.map((step, index) => {
          const quizTitles = step.quizTitles
            || (step.quizTitle ? [step.quizTitle] : []);
          return (
            <li key={step.id} id={`shag-${step.id}`} className="oge-path-step">
              <div className="oge-path-step-num" aria-hidden>
                {index + 1}
              </div>
              <div className="oge-path-step-body">
                <h2 className="oge-path-step-title">{step.title}</h2>
                {step.lead ? <p className="oge-path-step-lead text-secondary">{step.lead}</p> : null}
                <ol className="oge-path-actions">
                  {step.konspekts.map((k) => (
                    <li key={k.slug}>
                      <span className="oge-path-kind">Прочитать</span>
                      <Link to={`/konspekty/${k.slug}`}>{k.title}</Link>
                    </li>
                  ))}
                  {quizTitles.map((title) => {
                    const quiz = quizByTitle.get(title);
                    return (
                      <li key={title}>
                        <span className="oge-path-kind">Тест</span>
                        {user && quiz ? (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={startingId === quiz.id}
                            onClick={() => void startQuiz(quiz)}
                          >
                            {startingId === quiz.id ? 'Открываю…' : title}
                          </button>
                        ) : (
                          <span className="text-secondary">
                            {title}
                            {!loading && !quiz ? ' — не найден, запусти сид' : ''}
                          </span>
                        )}
                        {quiz?.task_count ? (
                          <span className="text-secondary oge-path-qcount">
                            {quiz.task_count} вопр.
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                  {step.writeTag ? (
                    <li>
                      <span className="oge-path-kind">Задачи</span>
                      <Link to={writeCatalogHref(step.writeTag)}>Написать программу</Link>
                    </li>
                  ) : null}
                </ol>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
